import React, { useState } from "react";
import useSWR from "swr";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  DatePicker,
  Select,
  Input,
  Switch,
  Space,
  Button,
  Modal,
  message,
  Timeline,
  Empty,
  Tabs,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Legend,
  CartesianGrid,
} from "recharts";
import dayjs, { Dayjs } from "dayjs";
import {
  getActiveUsers,
  getProductFunnel,
  getDailyFunnel,
  getApiStats,
  getMonitoringLogs,
  getFunnelStageUsers,
  getUserJourney,
  getRewardStats,
  getTabEntry,
  getCommunityMetrics,
  getPushStats,
} from "../api/domainApi";
import type {
  FunnelStage,
  StageChurn,
  DailyFunnelPoint,
  MonCountItem,
  LogItem,
  StageUsersRes,
  UserJourneyRes,
  ApiStatsRes,
  RewardBreakdown,
  RewardFailure,
  CommunityEvent,
  PushDaily,
} from "../types/domain";
import PageHeader from "../components/common/PageHeader";

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** 채널 선택값 → 화면 표기. 서버는 원시값(STORE/ADMIN/ALL)을 그대로 돌려준다. */
const CHANNEL_LABEL: Record<string, string> = { STORE: "STORE", ADMIN: "ADMIN", ALL: "전체" };

const fmtFrom = (d: Dayjs) => d.format("YYYY-MM-DD[T]00:00:00");
const fmtTo = (d: Dayjs) => d.format("YYYY-MM-DD[T]23:59:59");
const statusColor = (s: string) =>
  s === "SUCCESS" ? "green" : s === "ERROR" ? "red" : "orange";

const DashboardPage: React.FC = () => {
  const { token } = theme.useToken(); // 라이트/다크 적응 색상 토큰
  const COLOR_OK = token.colorSuccess;
  const COLOR_BAD = token.colorError;
  // 차트 다중 시리즈 팔레트 (양 모드 가시성 확보)
  const CH = { primary: token.colorPrimary, green: "#52c41a", gold: "#faad14", red: token.colorError };
  const chartTick = { fontSize: 11, fill: token.colorTextSecondary };
  const chartTooltip = {
    contentStyle: {
      background: token.colorBgElevated,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: 6,
    },
    labelStyle: { color: token.colorText },
    itemStyle: { color: token.colorText },
  };

  // 전역 필터 (API 헬스 / 로그 탐색기에 적용)
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [channel, setChannel] = useState("STORE");
  const from = fmtFrom(range[0]);
  const to = fmtTo(range[1]);

  // 로그 탐색기 필터
  const [logStatus, setLogStatus] = useState<string | undefined>();
  const [logKw, setLogKw] = useState("");
  const [logKwInput, setLogKwInput] = useState("");
  const [logUserId, setLogUserId] = useState("");
  const [onlyBugs, setOnlyBugs] = useState(false);
  const [logPage, setLogPage] = useState(1);

  // 퍼널 이탈 드릴다운 모달
  const [stageModal, setStageModal] = useState<{
    open: boolean;
    label: string;
    loading: boolean;
    data?: StageUsersRes;
  }>({ open: false, label: "", loading: false });

  // 대시보드 탭 (상단 현황요약은 탭 밖에 상시 노출)
  const [activeTab, setActiveTab] = useState("conv");

  // 사용자 여정
  const [journeyInput, setJourneyInput] = useState("");
  const [journey, setJourney] = useState<UserJourneyRes | null>(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [selectedPetId, setSelectedPetId] = useState<number | undefined>();

  const { data: active, isLoading: l1 } = useSWR("/dash/active", getActiveUsers);
  const { data: funnel, isLoading: l2 } = useSWR("/dash/funnel", getProductFunnel);
  const { data: dailyFunnel, isLoading: l5 } = useSWR("/dash/daily-funnel", getDailyFunnel);
  const { data: reward, isLoading: l6 } = useSWR("/dash/reward", getRewardStats);
  const { data: tabEntry, isLoading: l7 } = useSWR("/dash/tab-entry", getTabEntry);
  const { data: community, isLoading: l8 } = useSWR("/dash/community", getCommunityMetrics);
  const { data: push, isLoading: l9 } = useSWR(
    `/dash/push?${from}|${to}`,
    () => getPushStats({ from, to }),
  );
  // 🔴 상단 요약 전용 — 조회 기간/채널 <b>선택과 무관하게</b> 항상 서버 기본(최근 7일 · STORE).
  //    상단은 "지금 서비스가 건강한가" 를 늘 같은 잣대로 보여주는 자리라, 아래 탭의 기간을
  //    따라가면 30일을 고른 순간 배지와 라벨이 전부 다른 뜻이 된다(2026-09-29 교정).
  const { data: apiTop, isLoading: l3top } = useSWR("/dash/api-summary", () => getApiStats());

  // 🔴 상단 배지 전용 푸시 — apiTop 과 같은 이유로 조회 기간을 따르지 않는다.
  //    아래 푸시 카드(push)는 조회 기간을 따르므로, 배지에 그걸 쓰면 기간을 30일로 바꾼 순간
  //    "최근 7일" 이라고 적힌 배지가 30일치로 판정하게 된다.
  const { data: pushTop } = useSWR("/dash/push-summary", () => getPushStats());

  // 아래 탭(시스템 성능·로그) 전용 — 이쪽이 조회 기간/채널을 따른다.
  const { data: api, isLoading: l3 } = useSWR(
    `/dash/api?${from}|${to}|${channel}`,
    () => getApiStats({ from, to, channel }),
  );
  const { data: logs, isLoading: l4 } = useSWR(
    `/dash/logs?${from}|${to}|${channel}|${logStatus}|${logKw}|${logUserId}|${onlyBugs}|${logPage}`,
    () =>
      getMonitoringLogs({
        from,
        to,
        channel,
        status: logStatus,
        userId: logUserId ? Number(logUserId) : undefined,
        onlyServerBugs: onlyBugs,
        keyword: logKw || undefined,
        page: logPage - 1,
        size: 20,
      }),
    { keepPreviousData: true },
  );

  const scrollToLogs = () => {
    setActiveTab("logs"); // 로그 탭으로 전환 후 스크롤
    setTimeout(() => document.getElementById("log-explorer")?.scrollIntoView({ behavior: "smooth" }), 120);
  };

  /**
   * 상단 배지의 "… 보기 →" 이동. 탭만 바꾸면 그 탭의 첫 카드가 보여서,
   * 예컨대 "푸시 발송 현황 보기" 를 눌러도 보상 카드가 먼저 나온다 → 해당 카드까지 스크롤.
   * 탭 내용이 그려진 뒤에 찾아야 하므로 scrollToLogs 와 같은 지연(120ms)을 쓴다.
   */
  const goToIssue = (tab: string, anchorId?: string) => {
    setActiveTab(tab);
    if (anchorId) {
      setTimeout(() => document.getElementById(anchorId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  };

  // 퍼널 이탈 드릴다운: 그 단계 도달 후 다음 단계 미진입 사용자
  const openStageDropoff = async (st: FunnelStage) => {
    setStageModal({ open: true, label: st.label, loading: true });
    try {
      const data = await getFunnelStageUsers(st.key, true);
      setStageModal({ open: true, label: st.label, loading: false, data });
    } catch (e: any) {
      message.error(e?.message ?? "조회 실패");
      setStageModal((s) => ({ ...s, loading: false }));
    }
  };

  const copyToClipboard = async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(okMsg);
    } catch {
      message.error("복사 실패 (브라우저 권한 확인)");
    }
  };

  // 사용자 여정 조회
  const loadJourney = async () => {
    const uid = Number(journeyInput);
    if (!uid) {
      message.warning("userId 를 입력하세요");
      return;
    }
    setJourneyLoading(true);
    try {
      const data = await getUserJourney(uid);
      setJourney(data);
      setSelectedPetId(data.pets?.[0]?.petId);
    } catch (e: any) {
      message.error(e?.message ?? "조회 실패");
    } finally {
      setJourneyLoading(false);
    }
  };

  // 이탈 사용자 클릭 → 로그 탐색기에서 그 사용자 추적
  const traceUser = (uid: number) => {
    setLogUserId(String(uid));
    setOnlyBugs(false);
    setLogStatus(undefined);
    setLogKw("");
    setLogKwInput("");
    setLogPage(1);
    setStageModal((s) => ({ ...s, open: false }));
    setTimeout(scrollToLogs, 50);
  };

  // 🔴 버그 드릴다운: onlyBugs ON + (엔드포인트 메서드 키워드)
  const drillBugs = (endpoint?: string) => {
    setOnlyBugs(true);
    setLogStatus(undefined);
    const method = endpoint ? endpoint.split(".").pop() ?? "" : "";
    setLogKw(method);
    setLogKwInput(method);
    setLogUserId("");
    setLogPage(1);
    setTimeout(scrollToLogs, 50);
  };

  // ─── columns ───
  const bugEpCols: ColumnsType<MonCountItem> = [
    {
      title: "엔드포인트",
      dataIndex: "key",
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => drillBugs(v)}>
          {v}
        </Button>
      ),
    },
    { title: "건수", dataIndex: "count", width: 70 },
  ];
  const bizMsgCols: ColumnsType<MonCountItem> = [
    { title: "메시지", dataIndex: "key", ellipsis: { showTitle: true } },
    { title: "건수", dataIndex: "count", width: 70 },
  ];

  const dailyFunnelCols: ColumnsType<DailyFunnelPoint> = [
    { title: "일자", dataIndex: "date", width: 70, fixed: "left", render: (v: string) => v?.slice(5) },
    { title: "평가시작", dataIndex: "assessmentStarted", align: "right" },
    { title: "평가완료", dataIndex: "assessmentCompleted", align: "right" },
    { title: "트레이닝시작", dataIndex: "trainingStarted", align: "right" },
    { title: "회차수행", dataIndex: "iterationDone", align: "right" },
    { title: "트레이닝완료", dataIndex: "trainingCompleted", align: "right" },
  ];

  const churnCols: ColumnsType<StageChurn> = [
    {
      title: "단계",
      dataIndex: "label",
      render: (v: string, r) =>
        r.key === "care_started" ? (
          <span>
            {v} <Tag color="red" style={{ marginLeft: 4 }}>최대 타겟</Tag>
          </span>
        ) : (
          v
        ),
    },
    { title: "정체 인원", dataIndex: "stuck", align: "right", width: 100 },
    {
      title: "비중",
      key: "ratio",
      align: "right",
      width: 80,
      render: (_: unknown, r) => {
        const total = (funnel?.churn ?? []).reduce((a, c) => a + c.stuck, 0);
        return total > 0 ? `${Math.round((r.stuck / total) * 1000) / 10}%` : "-";
      },
    },
    {
      title: "3일+ 미활동",
      dataIndex: "inactive3",
      align: "right",
      width: 110,
      render: (v: number) => <span style={{ color: token.colorWarning }}>{v}</span>,
    },
    {
      title: "8일+ 미활동",
      dataIndex: "inactive8",
      align: "right",
      width: 110,
      render: (v: number) => <span style={{ color: token.colorError }}>{v}</span>,
    },
    { title: "14일+ 미활동", dataIndex: "inactive14", align: "right", width: 110 },
  ];

  // 가용성은 숫자만 띄우면 "어떻게 100% 가 나오는 건지" 를 매번 되묻게 된다(2026-09-29 운영 문의).
  // 정의가 (1 − 5xx/전체요청) 이므로 분모·분자를 그대로 붙여 화면에서 근거가 보이게 한다.
  // 🔴 채널도 같이 적는다 — 상단 고정 카드는 인자 없이 호출해 서버 기본값(STORE)만 집계하는데,
  //    "가용성 (최근 7일)" 이라고만 쓰면 전 채널로 읽혀 어드민 5xx 가 없는 것처럼 보인다.
  const availabilityBasis = (s?: ApiStatsRes) =>
    s ? (
      <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
        {CHANNEL_LABEL[s.channel] ?? s.channel} {(s.total ?? 0).toLocaleString()}건 중 5xx{" "}
        {(s.http5xxCount ?? 0).toLocaleString()}건
      </Text>
    ) : null;

  // 실패 건별 목록 — "누가 실패했는지" 를 DB 안 뒤지고 보기 위한 표 (운영 요청 2026-09-29).
  // 🔴 FAILED ≠ 미발급 — 타임아웃/duplicated-coupon 처럼 실제로는 발급된 건도 여기 들어온다.
  const rewardFailCols: ColumnsType<RewardFailure> = [
    {
      title: "시각",
      dataIndex: "issuedAt",
      width: 105,
      render: (v: string) => (v ? v.slice(5, 16).replace("T", " ") : "-"),
    },
    {
      title: "사용자",
      dataIndex: "userId",
      width: 130,
      render: (_: unknown, r: RewardFailure) => (
        <>
          <div>{r.userId ?? "-"}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.clayfulId ?? "(SC 미이용)"}
          </Text>
        </>
      ),
    },
    { title: "유형", dataIndex: "rewardType", width: 70 },
    { title: "사유", dataIndex: "failReason", ellipsis: true },
  ];

  const rewardCols: ColumnsType<RewardBreakdown> = [
    { title: "유형", dataIndex: "rewardType", width: 80 },
    { title: "출처", dataIndex: "sourceType", width: 100 },
    {
      title: "상태",
      dataIndex: "status",
      width: 90,
      render: (v: string) => <Tag color={v === "FAILED" ? "red" : "green"}>{v}</Tag>,
    },
    { title: "건수", dataIndex: "count", width: 70 },
  ];

  const pushCols: ColumnsType<PushDaily> = [
    { title: "일자", dataIndex: "date", width: 100, render: (d: string) => d?.slice(5) },
    { title: "성공", dataIndex: "sent", width: 70 },
    { title: "미발송", dataIndex: "noRecipient", width: 80 },
    {
      title: "실패",
      dataIndex: "failed",
      width: 70,
      render: (v: number) => (v > 0 ? <Text style={{ color: COLOR_BAD }}>{v}</Text> : v),
    },
  ];

  const communityEventCols: ColumnsType<CommunityEvent> = [
    { title: "항목", dataIndex: "label" },
    { title: "actor(기기)", dataIndex: "actor", width: 90 },
    { title: "유저", dataIndex: "users", width: 70 },
  ];

  const journeyColor = (cat: string) =>
    cat === "평가" ? "blue" : cat === "트레이닝" ? "green" : "gray";

  const logCols: ColumnsType<LogItem> = [
    { title: "시각", dataIndex: "requestTime", width: 165, render: (v: string) => v?.replace("T", " ").slice(0, 19) },
    {
      title: "상태",
      dataIndex: "status",
      width: 80,
      render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag>,
    },
    { title: "코드", dataIndex: "httpStatusCode", width: 60 },
    { title: "메서드", dataIndex: "httpMethod", width: 70 },
    { title: "엔드포인트", dataIndex: "endpoint", ellipsis: { showTitle: true } },
    { title: "userId", dataIndex: "userId", width: 80 },
    { title: "ms", dataIndex: "executionTimeMs", width: 70 },
  ];

  // ─── KPI 요약 계산 ───
  const fUsers = (k: string) => funnel?.stages.find((s) => s.key === k)?.users ?? 0;
  const careStartRate =
    fUsers("assessment_started") > 0
      ? Math.round((fUsers("training_started") / fUsers("assessment_started")) * 1000) / 10
      : 0;

  // ─── 주간 추세(WoW) — 일별 데이터로 최근 7일 vs 이전 7일 ───
  const last7Sum = <T,>(arr: T[] | undefined, key: keyof T) =>
    (arr ?? []).slice(-7).reduce((a, r) => a + (Number(r[key]) || 0), 0);
  const wow = <T,>(arr: T[] | undefined, key: keyof T): number | null => {
    if (!arr || arr.length < 14) return null;
    const cur = arr.slice(-7).reduce((a, r) => a + (Number(r[key]) || 0), 0);
    const prev = arr.slice(-14, -7).reduce((a, r) => a + (Number(r[key]) || 0), 0);
    if (prev === 0) return null;
    return Math.round(((cur - prev) / prev) * 1000) / 10;
  };
  const activeWow = wow(active?.dailyTrend, "activeUsers");
  const assessWow = wow(dailyFunnel?.days, "assessmentStarted");
  const trainWow = wow(dailyFunnel?.days, "trainingStarted");
  const assess7 = last7Sum(dailyFunnel?.days, "assessmentStarted");
  const trainStart7 = last7Sum(dailyFunnel?.days, "trainingStarted");
  const wowTag = (v: number | null) =>
    v == null ? null : (
      <Text style={{ fontSize: 12, color: v >= 0 ? COLOR_OK : COLOR_BAD }}>
        {v >= 0 ? "▲" : "▼"} {Math.abs(v)}%
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>전주 대비</Text>
      </Text>
    );

  // ─── 신호등 ───
  // 🔴 판정 구간은 전부 최근 7일로 통일(2026-09-28). 보상만 누적이던 탓에 역대 실패 1건으로
  //    영구히 🔴 위험이 떠 있었다 — 서버가 reward.total/failed 를 7일 기준으로 내려준다.
  const toStatus = (issues: string[], critical: boolean, okReason: string) =>
    issues.length === 0
      ? { color: "green", label: "🟢 정상", reason: okReason }
      : critical
        ? { color: "red", label: "🔴 위험", reason: issues.join(" · ") }
        : { color: "gold", label: "🟡 주의", reason: issues.join(" · ") };

  // API 전용 — "시스템 성능" 탭 배지가 쓴다. 보상 실패를 섞지 않는다:
  // 섞으면 가용성 100% · p95 정상인데도 탭이 빨개져서 API 장애로 오독된다.
  const apiIssues: string[] = [];
  if ((api?.availabilityPct ?? 100) < 99.5) apiIssues.push(`가용성 ${api?.availabilityPct}%`);
  if ((api?.latency?.p95Ms ?? 0) > 500) apiIssues.push(`p95 ${api?.latency?.p95Ms}ms`);
  if ((api?.bugRatePct ?? 0) > 0.3) apiIssues.push(`버그율 ${api?.bugRatePct}%`);
  const apiStatus = toStatus(
    apiIssues,
    (api?.availabilityPct ?? 100) < 99.5 || (api?.latency?.p95Ms ?? 0) > 800,
    "API 지표 정상",
  );

  // 상단 종합 배지 전용 — 같은 판정을 apiTop(항상 7일)으로 한 번 더 한다.
  // 🔴 apiIssues(선택 기간)를 상단에 쓰면, 기간을 30일로 바꾼 순간 상단 신호등의 뜻이 달라진다.
  const apiIssuesTop: string[] = [];
  if ((apiTop?.availabilityPct ?? 100) < 99.5) apiIssuesTop.push(`가용성 ${apiTop?.availabilityPct}%`);
  if ((apiTop?.latency?.p95Ms ?? 0) > 500) apiIssuesTop.push(`p95 ${apiTop?.latency?.p95Ms}ms`);
  if ((apiTop?.bugRatePct ?? 0) > 0.3) apiIssuesTop.push(`버그율 ${apiTop?.bugRatePct}%`);
  const apiCriticalTop =
    (apiTop?.availabilityPct ?? 100) < 99.5 || (apiTop?.latency?.p95Ms ?? 0) > 800;

  // 보상 전용 — "보상 발급 현황" 카드 배지가 쓴다.
  const rewardIssues: string[] = [];
  if ((reward?.failed ?? 0) > 0) rewardIssues.push(`실패 ${reward?.failed}건`);
  const rewardStatus = toStatus(rewardIssues, (reward?.failed ?? 0) > 0, "최근 7일 실패 없음");

  // 푸시 전용 — 상단 배지가 쓴다. 🔴 미발송(토큰 없음)은 장애가 아니므로 실패(FCM 오류)만 본다.
  //    이 구분이 없으면 대상자 대부분이 앱 미설치인 날 배지가 늘 빨개진다.
  const pushIssues: string[] = [];
  if ((pushTop?.failed ?? 0) > 0) pushIssues.push(`푸시실패 ${pushTop?.failed}건`);

  // 종합 — 상단 요약. API + 보상 + 푸시를 본다. 🔑 셋 다 최근 7일 고정이라 기준이 하나다.
  const healthIssues = [...apiIssuesTop, ...rewardIssues.map((t) => `보상${t}`), ...pushIssues];
  const status = toStatus(
    healthIssues,
    apiCriticalTop || rewardStatus.color === "red",
    "모든 헬스 지표 정상 (최근 7일)",
  );

  return (
    <div>
      <PageHeader title="운영 대시보드" />

      {/* 🌟 현황 요약 — 종합 상태(신호등) + 핵심 지표 + 주간 추세(WoW) */}
      <Card style={{ marginBottom: 16, background: token.colorFillAlter }} loading={l3top}>
        <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <Tag color={status.color} style={{ fontSize: 14, padding: "2px 12px", margin: 0 }}>
            {status.label}
          </Tag>
          <Text type="secondary" style={{ fontSize: 13 }}>{status.reason}</Text>
          {/* 이상을 알아챈 다음 "어디를 보나" 가 끊겨 있어 탭 이름을 유추해야 했다.
              이슈 종류별로 해당 탭으로 바로 보낸다. */}
          {healthIssues.length > 0 && (
            <Space size={4} wrap>
              {apiIssuesTop.length > 0 && (
                <Button size="small" type="link" onClick={() => goToIssue("sys", "api-health")}>
                  시스템 성능 보기 →
                </Button>
              )}
              {rewardIssues.length > 0 && (
                <Button size="small" type="link" onClick={() => goToIssue("reward", "reward-stats")}>
                  보상 발급 현황 보기 →
                </Button>
              )}
              {pushIssues.length > 0 && (
                <Button size="small" type="link" onClick={() => goToIssue("reward", "push-stats")}>
                  푸시 발송 현황 보기 →
                </Button>
              )}
            </Space>
          )}
        </div>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic title="오늘 활성 (DAU)" value={active?.dau ?? 0} loading={l1} />
            {wowTag(activeWow)}
          </Col>
          <Col span={4}>
            <Statistic title="평가 시작 (최근 7일)" value={assess7} loading={l5} />
            {wowTag(assessWow)}
          </Col>
          <Col span={4}>
            <Statistic title="트레이닝 시작 (최근 7일)" value={trainStart7} loading={l5} />
            {wowTag(trainWow)}
          </Col>
          <Col span={4}>
            <Statistic title="케어 착수율 (전체)" value={careStartRate} suffix="%" precision={1} loading={l2} />
          </Col>
          <Col span={4}>
            <Statistic
              title="가용성 (최근 7일)"
              value={apiTop?.availabilityPct ?? 100}
              suffix="%"
              precision={1}
              valueStyle={{ color: (apiTop?.availabilityPct ?? 100) < 99.5 ? COLOR_BAD : COLOR_OK }}
            />
            {availabilityBasis(apiTop)}
          </Col>
          <Col span={4}>
            <Statistic
              title="지연 p95 (최근 7일)"
              value={apiTop?.latency?.p95Ms ?? 0}
              suffix="ms"
              valueStyle={{ color: (apiTop?.latency?.p95Ms ?? 0) > 500 ? COLOR_BAD : COLOR_OK }}
            />
          </Col>
        </Row>
      </Card>

      {/* 전역 조회 필터 — 기간은 비즈니스(푸시 카드)·시스템 성능·로그 탭에, 채널은 시스템 성능·로그 탭에 적용 */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 12 } }}>
        <Space wrap>
          <Text type="secondary">조회 기간 (푸시·시스템 성능·로그)</Text>
          <RangePicker
            value={range}
            allowClear={false}
            presets={[
              { label: "오늘", value: [dayjs().startOf("day"), dayjs()] },
              { label: "최근 7일", value: [dayjs().subtract(7, "day"), dayjs()] },
              { label: "최근 30일", value: [dayjs().subtract(30, "day"), dayjs()] },
            ]}
            onChange={(v) => {
              if (v && v[0] && v[1]) {
                setRange([v[0], v[1]]);
                setLogPage(1);
              }
            }}
          />
          <Text type="secondary">채널 (시스템 성능·로그)</Text>
          <Select
            value={channel}
            style={{ width: 110 }}
            options={[
              { value: "STORE", label: "STORE" },
              { value: "ADMIN", label: "ADMIN" },
              // 일일 리포트의 DB 쿼리는 채널 조건이 없어 전 채널을 센다.
              // "전체" + 조회 기간을 리포트 날짜로 맞추면 같은 모수가 되어 숫자를 직접 대조할 수 있다.
              { value: "ALL", label: "전체" },
            ]}
            onChange={(v) => {
              setChannel(v);
              setLogPage(1);
            }}
          />
        </Space>
      </Card>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="large"
        items={[
          {
            key: "conv",
            label: "핵심 지표",
            children: (
              <>

      {/* 활성 사용자 (실사용 기준 — 네이티브 배경호출 제외) */}
      <Card title="활성 사용자 (실사용 rolling · 배경호출 제외)" style={{ marginBottom: 16 }} loading={l1}>
        <Row gutter={16}>
          <Col span={6}><Statistic title="DAU (최근 1일)" value={active?.dau ?? 0} /></Col>
          <Col span={6}><Statistic title="WAU (최근 7일)" value={active?.wau ?? 0} /></Col>
          <Col span={6}><Statistic title="MAU (최근 30일)" value={active?.mau ?? 0} /></Col>
          <Col span={6}><Statistic title="고착도 (DAU/MAU)" value={active?.stickinessPct ?? 0} suffix="%" /></Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card title="제품 퍼널 (전체기간)" loading={l2} style={{ height: "100%" }}>
            <div>
              {(funnel?.stages ?? []).map((s) => {
                const weak = s.fromPrevPct != null && s.fromPrevPct < 50;
                return (
                  <div key={s.key} style={{ marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3 }}>
                      <span>{s.label}</span>
                      <span>
                        <b>{s.users}</b>
                        <Text type="secondary" style={{ marginLeft: 8 }}>{s.fromTopPct}%</Text>
                        {s.fromPrevPct != null && (
                          <Text style={{ marginLeft: 8, color: weak ? COLOR_BAD : token.colorTextSecondary }}>
                            직전 {s.fromPrevPct}%
                          </Text>
                        )}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, background: token.colorFillSecondary, borderRadius: 4, height: 20 }}>
                        <div
                          style={{
                            width: `${Math.max(s.fromTopPct, 1.5)}%`,
                            background: weak ? token.colorError : token.colorPrimary,
                            height: "100%",
                            borderRadius: 4,
                            transition: "width .3s",
                          }}
                        />
                      </div>
                      {s.key === "course_completed" ? (
                        <Tag color="green" style={{ margin: 0 }}>최종</Tag>
                      ) : (
                        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => openStageDropoff(s)}>
                          이탈
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {(funnel?.stages?.length ?? 0) === 0 && <Empty description="데이터 없음" />}
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="일자별 활성 사용자 (최근 30일)" loading={l1} style={{ height: "100%" }}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={active?.dailyTrend ?? []} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis dataKey="date" tick={chartTick} tickFormatter={(d: string) => d?.slice(5)} />
                <YAxis tick={chartTick} allowDecimals={false} />
                <RTooltip {...chartTooltip} />
                <Line type="monotone" dataKey="activeUsers" name="활성 사용자" stroke={CH.primary} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 단계별 정체 코호트 (미활동 경과일 · 리마인더 타겟) */}
      {(funnel?.churn ?? []).some((c) => c.stuck > 0) && (
        <Card
          title="단계별 정체 코호트 (마지막 활동 후 미활동 경과일 · 리마인더 타겟)"
          style={{ marginBottom: 16 }}
          loading={l2}
        >
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            columns={churnCols}
            dataSource={(funnel?.churn ?? []).filter((c) => c.stuck > 0)}
          />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            미활동 = 마지막 도메인 활동 후 경과일(서버 오늘 기준) · 누적(3일+는 8·14일+ 포함) · 코스완료는 재참여 아님
          </Text>
        </Card>
      )}

      {/* 일별 퍼널 현황 + 사용자 여정 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card
            title="일별 퍼널 현황 (전체기간 · 그 날 발생 인원 · 도메인 기준)"
            loading={l5}
            style={{ height: "100%" }}
          >
            <Table
              rowKey="date"
              size="small"
              pagination={false}
              scroll={{ y: 240 }}
              columns={dailyFunnelCols}
              dataSource={dailyFunnel?.days ?? []}
              summary={(rows) => {
                const keys = [
                  "assessmentStarted",
                  "assessmentCompleted",
                  "trainingStarted",
                  "iterationDone",
                  "trainingCompleted",
                ] as const;
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>합계</Table.Summary.Cell>
                      {keys.map((k, i) => (
                        <Table.Summary.Cell key={k} index={i + 1} align="right">
                          <b>{rows.reduce((a, r) => a + (r[k] ?? 0), 0)}</b>
                        </Table.Summary.Cell>
                      ))}
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
              누적 퍼널과 다름 — '그 날 발생'(재평가로 여러 날 시작 시 각 날 카운트). 오늘은 진행중.
            </Text>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="개별 사용자 여정" style={{ height: "100%" }}>
            <Space style={{ marginBottom: 12 }}>
              <Input
                placeholder="userId"
                style={{ width: 130 }}
                value={journeyInput}
                onChange={(e) => setJourneyInput(e.target.value.replace(/[^0-9]/g, ""))}
                onPressEnter={loadJourney}
              />
              <Button type="primary" loading={journeyLoading} onClick={loadJourney}>
                조회
              </Button>
              {journey && (
                <Button size="small" onClick={() => traceUser(journey.userId as number)}>
                  로그 추적
                </Button>
              )}
            </Space>
            {!journey ? (
              <Empty description="userId 입력 후 조회" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : !journey.found ? (
              <Empty description={`#${journey.userId} 활동 없음`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <>
                <div style={{ marginBottom: 8 }}>
                  <Tag color="geekblue">커뮤니티 글 {journey.communityPosts}</Tag>
                  <Tag color="geekblue">댓글 {journey.communityComments}</Tag>
                </div>
                {journey.pets.length === 0 ? (
                  <Empty description="반려견 활동 없음" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <>
                    <Select
                      style={{ width: "100%", marginBottom: 12 }}
                      value={selectedPetId}
                      onChange={setSelectedPetId}
                      options={journey.pets.map((p) => ({
                        value: p.petId,
                        label: `🐶 ${p.petName ?? "이름없음"} (#${p.petId}) · ${p.events.length}건`,
                      }))}
                    />
                    {(() => {
                      const pet =
                        journey.pets.find((p) => p.petId === selectedPetId) ?? journey.pets[0];
                      return (
                        <div style={{ maxHeight: 300, overflowY: "auto" }}>
                          <Timeline
                            items={(pet.events ?? []).map((e) => ({
                              color: journeyColor(e.category),
                              children: (
                                <span>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {e.time.replace("T", " ").slice(0, 19)}
                                  </Text>{" "}
                                  <Tag>{e.category}</Tag> {e.label}
                                </span>
                              ),
                            }))}
                          />
                        </div>
                      );
                    })()}
                  </>
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>

              </>
            ),
          },
          {
            key: "sys",
            label: (
              <>
                시스템 성능{" "}
                {apiStatus.color !== "green" && (
                  <Tag color={apiStatus.color} style={{ margin: 0 }}>{apiStatus.label}</Tag>
                )}
              </>
            ),
            children: (
              <>
      {/* API 헬스 */}
      {/* 선택기는 "전체" 로 보여주는데 서버는 원시값 ALL 을 돌려주므로 라벨로 맞춘다 */}
      <Card id="api-health" title={`API 헬스 (${CHANNEL_LABEL[api?.channel ?? channel] ?? (api?.channel ?? channel)})`}
            loading={l3} style={{ marginBottom: 16 }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}><Statistic title="총 요청" value={api?.total ?? 0} /></Col>
          <Col span={4}><Statistic title="성공" value={api?.success ?? 0} /></Col>
          <Col span={4}>
            <div style={{ cursor: "pointer" }} onClick={() => drillBugs()}>
              <Statistic
                title="🔴 서버 버그 (클릭→로그)"
                value={api?.serverBugs ?? 0}
                valueStyle={{ color: (api?.serverBugs ?? 0) > 0 ? "#cf1322" : "#3f8600" }}
              />
            </div>
          </Col>
          <Col span={4}>
            <Statistic
              title="버그율"
              value={api?.bugRatePct ?? 0}
              suffix="%"
              valueStyle={{ color: (api?.bugRatePct ?? 0) > 1 ? "#cf1322" : "#3f8600" }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="🔴 가용성"
              value={api?.availabilityPct ?? 100}
              suffix="%"
              precision={1}
              valueStyle={{ color: (api?.availabilityPct ?? 100) < 99.5 ? COLOR_BAD : COLOR_OK }}
            />
            {availabilityBasis(api)}
          </Col>
          <Col span={4}>
            <Statistic
              title="🔴 지연 p95(ms)"
              value={api?.latency?.p95Ms ?? 0}
              valueStyle={{ color: (api?.latency?.p95Ms ?? 0) > 500 ? COLOR_BAD : COLOR_OK }}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}><Statistic title="🟡 비즈니스 예외" value={api?.businessExceptions ?? 0} /></Col>
          <Col span={4}><Statistic title="p50(ms)" value={api?.latency?.p50Ms ?? 0} /></Col>
          <Col span={4}><Statistic title="p99(ms)" value={api?.latency?.p99Ms ?? 0} /></Col>
          <Col span={4}>
            <Statistic
              title="지연 avg/max(ms)"
              value={api?.latency?.avgMs ?? 0}
              suffix={` / ${api?.latency?.maxMs ?? 0}`}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>🔴 서버 버그 엔드포인트 (클릭→해당 로그)</Text>
            <Table
              rowKey="key"
              columns={bugEpCols}
              dataSource={api?.topBugEndpoints}
              pagination={false}
              size="small"
              locale={{ emptyText: "버그 없음 👍" }}
              style={{ marginTop: 8 }}
            />
          </Col>
          <Col span={12}>
            <Text strong>🟡 비즈니스 예외 메시지 (정상 · UX 참고)</Text>
            <Table
              rowKey="key"
              columns={bizMsgCols}
              dataSource={api?.topBusinessExceptions}
              pagination={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </Col>
        </Row>
      </Card>
              </>
            ),
          },
          {
            key: "reward",
            label: "비즈니스",
            children: (
              <>

      {/* 보상 발급 + 케어탭 진입(회원/비회원) */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card
            id="reward-stats"
            title={
              <>
                보상 발급 현황{" "}
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>(최근 7일)</Text>{" "}
                {rewardStatus.color !== "green" && (
                  <Tag color={rewardStatus.color} style={{ margin: 0 }}>{rewardStatus.label}</Tag>
                )}
              </>
            }
            loading={l6}
            style={{ height: "100%" }}
          >
            <Row gutter={16} style={{ marginBottom: 4 }}>
              <Col span={8}><Statistic title="총 발급" value={reward?.total ?? 0} /></Col>
              <Col span={8}><Statistic title="성공" value={reward?.issued ?? 0} valueStyle={{ color: COLOR_OK }} /></Col>
              <Col span={8}>
                <Statistic
                  title="🔴 실패율"
                  value={reward?.failRatePct ?? 0}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: (reward?.failed ?? 0) > 0 ? COLOR_BAD : COLOR_OK }}
                />
              </Col>
            </Row>
            {/* 누적은 신호등 판정에 안 쓰지만 이력 추적용으로 남긴다 */}
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
              누적 {(reward?.totalAll ?? 0).toLocaleString()}건 · 실패 {reward?.failedAll ?? 0}건
            </Text>
            <Table<RewardBreakdown>
              rowKey={(r) => `${r.rewardType}-${r.sourceType}-${r.status}`}
              columns={rewardCols}
              dataSource={reward?.breakdown}
              pagination={false}
              size="small"
            />
            {(reward?.failReasons?.length ?? 0) > 0 && (
              <Table
                rowKey="key"
                columns={[
                  { title: "실패 사유 (누적)", dataIndex: "key" },
                  { title: "건수", dataIndex: "count", width: 80 },
                ]}
                dataSource={reward?.failReasons}
                pagination={false}
                size="small"
                style={{ marginTop: 8 }}
              />
            )}
            {(reward?.failures?.length ?? 0) > 0 && (
              <>
                <Text type="secondary" style={{ fontSize: 12, display: "block", margin: "12px 0 4px" }}>
                  실패 건별 (누적, 최대 50건) — 실패가 곧 미지급은 아님. 조치 전 실제 발급 여부 확인 필요
                </Text>
                <Table<RewardFailure>
                  rowKey="histId"
                  columns={rewardFailCols}
                  dataSource={reward?.failures}
                  pagination={false}
                  size="small"
                />
              </>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="케어탭 진입 — 회원 / 비회원 (7/16+)" loading={l7} style={{ height: "100%" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              비회원(anonymous_id)은 기기 단위 근사치 — 서버 로그로는 못 보는 유입.
            </Text>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={tabEntry?.daily ?? []} margin={{ top: 12, right: 12, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis dataKey="date" tick={chartTick} tickFormatter={(d: string) => d?.slice(5)} />
                <YAxis tick={chartTick} allowDecimals={false} />
                <RTooltip {...chartTooltip} />
                <Legend />
                <Line type="monotone" dataKey="anonActors" name="비회원 포함(기기)" stroke={CH.green} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="memberUsers" name="회원" stroke={CH.primary} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 푸시 발송 현황 + 일별 추이 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card id="push-stats" title="푸시 발송 현황 (조회 기간)" loading={l9} style={{ height: "100%" }}>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={6}><Statistic title="발송 대상" value={push?.total ?? 0} /></Col>
              <Col span={6}><Statistic title="성공" value={push?.sent ?? 0} valueStyle={{ color: COLOR_OK }} /></Col>
              <Col span={6}><Statistic title="미발송" value={push?.noRecipient ?? 0} /></Col>
              <Col span={6}>
                <Statistic
                  title="🔴 실패"
                  value={push?.failed ?? 0}
                  valueStyle={{ color: (push?.failed ?? 0) > 0 ? COLOR_BAD : COLOR_OK }}
                />
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: 12 }}>
              미발송(토큰 없음)은 장애 아님 — 실패(FCM 오류)와 분리. 성공률 {(push?.successRatePct ?? 0).toFixed(1)}%
            </Text>
            <Table<PushDaily>
              rowKey="date"
              columns={pushCols}
              dataSource={push?.daily}
              pagination={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="일별 푸시 추이 (조회 기간)" loading={l9} style={{ height: "100%" }}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={push?.daily ?? []} margin={{ top: 12, right: 12, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis dataKey="date" tick={chartTick} tickFormatter={(d: string) => d?.slice(5)} />
                <YAxis tick={chartTick} allowDecimals={false} />
                <RTooltip {...chartTooltip} />
                <Legend />
                <Line type="monotone" dataKey="sent" name="성공" stroke={CH.green} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="noRecipient" name="미발송" stroke={CH.primary} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="failed" name="실패" stroke={CH.red} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 커뮤니티 지표 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card title="커뮤니티 진입·활동 (7/16+)" loading={l8} style={{ height: "100%" }}>
            <Table<CommunityEvent>
              rowKey="event"
              columns={communityEventCols}
              dataSource={community?.events}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="커뮤니티 일별 작성 활동 (7/10+)" loading={l8} style={{ height: "100%" }}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={community?.daily ?? []} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                <XAxis dataKey="date" tick={chartTick} tickFormatter={(d: string) => d?.slice(5)} />
                <YAxis tick={chartTick} allowDecimals={false} />
                <RTooltip {...chartTooltip} />
                <Legend />
                <Bar dataKey="posts" name="글" fill={CH.primary} />
                <Bar dataKey="comments" name="댓글" fill={CH.green} />
                <Bar dataKey="replies" name="답글" fill={CH.gold} />
                <Bar dataKey="likes" name="좋아요" fill={CH.red} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

              </>
            ),
          },
          {
            key: "logs",
            label: "로그",
            children: (
              <Card id="log-explorer">
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            placeholder="상태"
            allowClear
            style={{ width: 120 }}
            value={logStatus}
            options={[
              { value: "SUCCESS", label: "SUCCESS" },
              { value: "ERROR", label: "ERROR" },
              { value: "FAIL", label: "FAIL" },
            ]}
            onChange={(v) => {
              setLogStatus(v);
              setLogPage(1);
            }}
          />
          <Input
            placeholder="userId"
            style={{ width: 110 }}
            value={logUserId}
            onChange={(e) => setLogUserId(e.target.value.replace(/[^0-9]/g, ""))}
            onPressEnter={() => setLogPage(1)}
          />
          <Input
            placeholder="URI/엔드포인트/에러 검색"
            style={{ width: 260 }}
            value={logKwInput}
            onChange={(e) => setLogKwInput(e.target.value)}
            onPressEnter={() => {
              setLogKw(logKwInput);
              setLogPage(1);
            }}
          />
          <Button
            onClick={() => {
              setLogKw(logKwInput);
              setLogPage(1);
            }}
          >
            검색
          </Button>
          <Space>
            <Text>서버버그만</Text>
            <Switch
              checked={onlyBugs}
              onChange={(v) => {
                setOnlyBugs(v);
                setLogPage(1);
              }}
            />
          </Space>
          <Button
            onClick={() => {
              setLogStatus(undefined);
              setLogKw("");
              setLogKwInput("");
              setLogUserId("");
              setOnlyBugs(false);
              setLogPage(1);
            }}
          >
            초기화
          </Button>
        </Space>
        <Table
          rowKey="logId"
          columns={logCols}
          dataSource={logs?.content}
          loading={l4}
          size="small"
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ fontSize: 12 }}>
                <div><b>URI:</b> {r.requestUri}</div>
                {r.errorType && (
                  <div style={{ color: "#cf1322" }}>
                    <b>{r.errorType}:</b> {r.errorMessage}
                  </div>
                )}
              </div>
            ),
          }}
          pagination={{
            current: logPage,
            pageSize: 20,
            total: logs?.page?.totalElements,
            onChange: setLogPage,
            showSizeChanger: false,
          }}
        />
      </Card>
            ),
          },
        ].sort(
          // 표시 순서: 핵심지표 → 비즈니스 → 시스템 성능 → 로그 (JSX 순서와 무관하게 정렬)
          (a, b) =>
            ["conv", "reward", "sys", "logs"].indexOf(String(a.key)) -
            ["conv", "reward", "sys", "logs"].indexOf(String(b.key)),
        )}
      />

      {/* 퍼널 이탈 사용자 드릴다운 모달 */}
      <Modal
        open={stageModal.open}
        title={`이탈 사용자 · "${stageModal.label}" 도달 후 다음 단계 미진입`}
        onCancel={() => setStageModal((s) => ({ ...s, open: false }))}
        footer={null}
        width={520}
      >
        {stageModal.loading ? (
          <div style={{ textAlign: "center", padding: 24 }}>불러오는 중…</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span>
                <Text strong>{stageModal.data?.total ?? 0}명</Text> 이탈
                {(stageModal.data?.total ?? 0) > (stageModal.data?.users.length ?? 0) && (
                  <Text type="secondary"> (상위 {stageModal.data?.users.length}명 표시)</Text>
                )}
                <Text type="secondary"> · 행 클릭 → 로그 추적</Text>
              </span>
              <Button
                size="small"
                disabled={!(stageModal.data?.users.length ?? 0)}
                onClick={() =>
                  copyToClipboard(
                    (stageModal.data?.users ?? []).map((u) => u.userId).join(", "),
                    `userId ${stageModal.data?.users.length ?? 0}개 복사됨`,
                  )
                }
              >
                userId 전체 복사
              </Button>
            </div>
            {(stageModal.data?.users.length ?? 0) === 0 ? (
              <Text type="secondary">이탈 사용자 없음 👍</Text>
            ) : (
              <Table
                rowKey="userId"
                size="small"
                dataSource={stageModal.data?.users}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                onRow={(r) => ({ onClick: () => traceUser(r.userId), style: { cursor: "pointer" } })}
                columns={[
                  {
                    title: "userId",
                    dataIndex: "userId",
                    width: 130,
                    render: (v: number) => (
                      <>
                        #{v}
                        <Button
                          size="small"
                          type="link"
                          style={{ padding: "0 6px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(String(v), `#${v} 복사됨`);
                          }}
                        >
                          복사
                        </Button>
                      </>
                    ),
                  },
                  {
                    title: "이메일",
                    dataIndex: "email",
                    render: (v: string | null) => v ?? <Text type="secondary">(없음)</Text>,
                  },
                ]}
              />
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default DashboardPage;
